import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Search, X, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Accueil', href: '/#accueil' },
    { label: 'Nos balades', href: '/#balades' },
    { label: 'Expérience', href: '/#experience' },
    { label: 'Boutique en ligne', href: '/boutique' },
    { label: 'Galerie', href: '/#galerie' },
    { label: 'Avis', href: '/#avis' },
    { label: 'FAQ', href: '/#faq' },
    { label: 'À propos', href: '/#concept' },
    { label: 'Carte de membre', href: '/carte-membre' },
    { label: 'Contact', href: '/#contact' }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-white py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Menu button */}
          <button
            onClick={() => setMenuOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl border border-slate-200 hover:border-[#E89534] hover:bg-orange-50/50 transition-all duration-300 group"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-5 h-5 text-slate-800 group-hover:text-[#E89534] transition-colors" />
            <span className="font-medium text-slate-800 group-hover:text-[#E89534] transition-colors">Menu</span>
          </button>

          {/* Logo : losange orange + cercle blanc + P noir */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="site-logo">
            <div className="relative w-11 h-11 rotate-45 bg-[#F4A340] rounded-md flex items-center justify-center shadow-sm group-hover:rotate-[135deg] transition-transform duration-700">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                <span className="-rotate-45 group-hover:-rotate-[135deg] transition-transform duration-700 text-slate-900 font-bold text-xl italic">p</span>
              </div>
            </div>
            <div className="leading-tight">
              <div className="text-2xl font-bold text-slate-900 tracking-tight">Pierre &amp; Edois</div>
              <div className="text-[10px] tracking-[0.2em] text-slate-500 font-medium">OLS · VISONS PLUS LOIN</div>
            </div>
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-1">
            <Link to="/boutique" className="relative p-3 rounded-full hover:bg-orange-50 transition-colors group" aria-label="Boutique">
              <ShoppingBag className="w-5 h-5 text-slate-800 group-hover:text-[#E89534] transition-colors" />
              {totalItems > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4A340] text-white text-[10px] font-bold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>
            <button
              onClick={() => setSearchOpen(true)}
              className="p-3 rounded-full hover:bg-orange-50 transition-colors group"
              aria-label="Rechercher"
            >
              <Search className="w-5 h-5 text-slate-800 group-hover:text-[#E89534] transition-colors" />
            </button>
          </div>
        </div>

        {/* Orange bottom border */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#F4A340] to-transparent"></div>
      </header>

      {/* Cloud decorative element */}
      <div className="fixed top-0 left-0 z-40 pointer-events-none">
        <div className="w-64 h-64 -ml-24 -mt-24 rounded-full bg-gradient-to-br from-sky-100 via-blue-50 to-green-50 opacity-70 blur-sm"></div>
      </div>

      {/* Compact dropdown Menu - top-left corner */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setMenuOpen(false)} />
          <div className="fixed top-20 left-6 z-[60] w-64 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-br from-[#F4A340] to-[#E89534]">
              <span className="text-white font-bold text-sm tracking-wide">Navigation</span>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Fermer le menu"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            <nav>
              <ul className="py-2">
                {navLinks.map((link, i) => (
                  <li key={link.href} style={{ animationDelay: `${i * 30}ms` }} className="animate-in slide-in-from-left-2 fade-in fill-mode-both duration-300">
                    <a
                      href={link.href}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-orange-50 hover:text-[#E89534] transition-colors"
                    >
                      {link.label}
                      <span className="text-slate-300 group-hover:text-[#F4A340]">›</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </>
      )}

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setSearchOpen(false)}>
          <div className="max-w-3xl mx-auto px-6 pt-32" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 border-b-2 border-[#F4A340] pb-4">
              <Search className="w-7 h-7 text-[#E89534]" />
              <input
                autoFocus
                type="text"
                placeholder="Rechercher une balade, une date, un tarif..."
                className="flex-1 text-2xl outline-none bg-transparent placeholder:text-slate-400"
              />
              <button onClick={() => setSearchOpen(false)} className="p-2 rounded-full hover:bg-slate-100">
                <X className="w-6 h-6 text-slate-600" />
              </button>
            </div>
            <div className="mt-8">
              <p className="text-sm uppercase tracking-widest text-slate-500 mb-4">Suggestions</p>
              <div className="flex flex-wrap gap-3">
                {['Balade Essentielle', 'Balade Avancée', 'Balade Premium', 'Ganvié', 'Porto-Novo', 'Aguégué'].map((s) => (
                  <button key={s} className="px-4 py-2 rounded-full bg-orange-50 hover:bg-[#F4A340] hover:text-white text-slate-700 transition-colors text-sm">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
